const REVEAL_CLASS = 'storefront-image-reveal'
const VISIBLE_CLASS = 'is-visible'
const COMPLETE_CLASS = 'is-reveal-complete'

// These components control image opacity themselves (carousels/crossfades).
// Revealing their visual wrapper keeps that behaviour intact.
const REVEAL_CONTAINER_SELECTOR = [
  '.image-container',
  '.catalog-grid-item__image-slider',
  '.rec-card__media',
  '.offers-carousel',
  '.pd-gallery--mobile .pd-gallery__slider',
  '.pd-gallery--desktop .pd-gallery__item',
].join(', ')

const EXCLUDED_ANCESTOR_SELECTOR = [
  '[data-image-reveal="off"]',
  'nav',
  'footer',
  '.app-loader',
  '.Vue-Toastification__container',
].join(', ')

const EXCLUDED_IMAGE_SELECTOR = [
  '[class*="logo"]',
  '[class*="icon"]',
  '.payment-card__logo',
].join(', ')

function isAdminRoute () {
  return window.location.pathname.startsWith('/configuracion')
}

function isDecorativeUiImage (image) {
  const src = image.currentSrc || image.getAttribute('src') || ''

  return image.matches(EXCLUDED_IMAGE_SELECTOR) ||
    image.closest(EXCLUDED_ANCESTOR_SELECTOR) !== null ||
    /\.svg(?:$|[?#])/i.test(src)
}

function resolveRevealTarget (image) {
  return image.closest(REVEAL_CONTAINER_SELECTOR) || image
}

/**
 * Installs one observer for all storefront content images, including images
 * inserted later by route changes, API responses, pagination and carousels.
 */
export function initStorefrontImageReveal (root) {
  if (!root || typeof window === 'undefined' || typeof MutationObserver === 'undefined') {
    return () => {}
  }

  const preparedTargets = new WeakSet()
  const targetState = new WeakMap()

  const reveal = (target) => {
    const state = targetState.get(target)
    if (!state || state.revealed || !state.inViewport || !state.imageReady) return

    state.revealed = true
    target.classList.add(VISIBLE_CLASS)

    const handleTransitionEnd = (event) => {
      if (event.target !== target || event.propertyName !== 'opacity') return
      target.classList.add(COMPLETE_CLASS)
      target.removeEventListener('transitionend', handleTransitionEnd)
    }
    target.addEventListener('transitionend', handleTransitionEnd)
  }

  const intersectionObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return

        const state = targetState.get(entry.target)
        if (state) state.inViewport = true
        reveal(entry.target)
        intersectionObserver.unobserve(entry.target)
      })
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -6% 0px',
    })
    : null

  const prepareImage = (image) => {
    if (!(image instanceof HTMLImageElement) || isAdminRoute() || isDecorativeUiImage(image)) return

    const target = resolveRevealTarget(image)
    if (preparedTargets.has(target)) return

    preparedTargets.add(target)
    target.classList.add(REVEAL_CLASS)

    const state = {
      imageReady: image.complete,
      inViewport: intersectionObserver === null,
      revealed: false,
    }
    targetState.set(target, state)

    if (!state.imageReady) {
      const handleReady = () => {
        state.imageReady = true
        reveal(target)
      }
      image.addEventListener('load', handleReady, { once: true })
      image.addEventListener('error', handleReady, { once: true })
    }

    if (intersectionObserver) intersectionObserver.observe(target)
    reveal(target)
  }

  const scan = (node) => {
    if (!(node instanceof Element)) return
    if (node.matches('img')) prepareImage(node)
    node.querySelectorAll('img').forEach(prepareImage)
  }

  const mutationObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach(scan)
    })
  })

  // Start before Vue mounts so initial images are prepared before first paint.
  scan(root)
  mutationObserver.observe(root, { childList: true, subtree: true })

  return () => {
    mutationObserver.disconnect()
    if (intersectionObserver) intersectionObserver.disconnect()
  }
}
